import type { PlanDetailsCopy } from '../planDetails'

const zh: PlanDetailsCopy = {
  fieldLabels: {
    current_age: '您的年龄',
    retirement_age: '退休时间',
    annual_income: '年收入',
    annual_savings: '年储蓄',
    inflation: '通胀率',
    realization_tax_rate: '资本利得税税率',
    cost_basis: '成本基础',
    w0: '当前可投资资产',
    primary_residence: '您的自住房',
    total_investable: '可投资资产',
    expected_return: '预期收益率',
    volatility: '波动率',
    horizon: '规划期限',
    jurisdiction: '税务居民身份',
    risk_aversion: '风险承受能力',
    birth_year: '出生年份',
    birth_date: '出生日期',
    marital_status: '婚姻状况',
    dependents: '受抚养人',
    dependent_ages: '受抚养人年龄',
    children: '子女',
    children_birth_years: '子女出生年份',
    partner_age: '配偶年龄',
    partner_annual_income: '配偶年收入',
    partner_retirement_age: '配偶退休年龄',
    plan_to_age: '规划截止年龄',
    gross_annual_income: '税前年收入',
    net_income: '净收入',
    annual_spending: '年支出',
    essential_spending: '必要支出',
    flexible_spending: '弹性支出',
    investments_total: '投资总额',
    total_investable_wealth: '可投资资产总额',
    portfolio_mix: '投资组合配置',
    cgt_rate: '资本收益税率',
    interest_rate: '利率',
    dividend_yield: '股息率',
    wealth_tax_rate: '财富税税率',
    filing_jurisdictions: '申报纳税的国家',
    citizenship: '国籍',
    tax_residency: '申报的税务居民身份',
    state: '州或省',
    region: '地区',
    city: '城市',
    locality: '市镇',
    church_tax_applicable: '须缴纳教会税',
    life_cover: '人寿保险保额',
    income_protection: '收入保障保险',
    employer_match: '雇主配缴',
    pension: '养老金',
    state_pension: '国家养老金',
    social_security: '社会保障',
    wage_growth: '工资增长',
    stability: '收入稳定性',
    has_will: '已立遗嘱',
    risk_attitude: '风险态度',
  },
  keyLabels: {
    goal_priority: '目标排序',
    current_age: '年龄',
    inflation: '通胀假设',
    jurisdiction: '税务居民身份',
    realization_tax_rate: '为实现目标出售资产时的税负',
    citizenship: '国籍',
    total_investable: '可投资财富',
    annual_income: '年收入',
    annual_savings: '年储蓄',
    risk_tier: '风险等级',
    retirement_age: '退休年龄',
    target_wealth: '目标财富',
    primary_residence_sellable: '出售自住房 / 换小房',
  },
  channelLabels: {
    web_levers: '网页调节项',
    web_cockpit: '决策面板',
    web: '网页',
    chat: 'AI 助手',
    mcp: 'AI 助手',
    full_review: '全面审查',
    full_plan: '完整计划',
    what_if: '情景模拟',
  },
  objectiveLabels: {
    status_quo: '维持现状',
    max_goal_probability: '成功概率最高',
    lowest_drawdown: '最大回撤最小',
    highest_growth: '增长最高',
    fastest_to_goal: '最早实现目标',
  },
  choices: {
    interest_only: '只付利息',
    fixed_rate_amortizing: '分期还本（固定利率）',
    variable_rate_amortizing: '分期还本（浮动利率）',
    repayment: '分期还本',
    refinance: '按市场利率再融资',
    repay: '一次性全额还清',
  },
  values: {
    yearsShort: (n) => `${n} 年`,
    shareAbout: (pct) => `约其价值的 ${pct}`,
    sharePart: '其部分价值',
    aroundAge: (age) => `（约 ${age} 岁时）`,
    inAboutYears: (n) => `（约 ${n} 年后）`,
    maySellFor: (share, when) => `可出售以释放${share}${when}，但仅限于这样做能改善计划时。`,
    maySell: '仅在能改善计划时可出售。',
    structuredFact: '结构化信息',
  },
  ask: { talkThrough: '与 MyCIO 深入讨论' },
  review: {
    openInputCheckFailed: '待补充信息检查未能完成。请查看下方的审查状态。',
    finishedLoading: '您的审查已完成，正在加载结果…',
    draftReady: '您的初步审查已就绪——下方列出已完成的分析、未能计算的部分，以及补全所需回答的问题。',
    finishedNotLoaded: '您的审查已完成，但结果暂时无法加载。正在重试——不会重新计算。',
    fetchFailed: '您的审查已完成，但暂时无法获取。结果已保存——请稍后再试，无需重新计算。',
    notComputed: (list) => `本次运行未计算：${list}。`,
    changesNotSaved: (_n, f) => `有 ${f} 项更改未保存——请查看“您的信息”。`,
  },
  facts: {
    chooseOption: '请选择一个选项。',
    couldNotSave: '无法保存该数值，您的计划未作更改。',
    couldNotUndo: '无法撤销该更改。',
    choosePlaceholder: '请选择…',
    confirmedBadge: '已确认——如需修改请在聊天中提出',
    confirmedTitle: (was) => `您此前已将此项确认为 ${was}。如需替换，请在聊天中告诉 MyCIO。`,
  },
  assumptionsToConfirm: { anInput: '某项输入', assumedSuffix: (v) => `——假设值：${v}` },
  editor: {
    enterNumber: '请输入数字。',
    enterNumberFor: (l) => `请为“${l}”输入数字。`,
    enterPercentFor: (l) => `请为“${l}”输入百分比。`,
    chooseYesNoFor: (l) => `请为“${l}”选择“是”或“否”。`,
    semicolonBetween: (l) => `“${l}”的各项之间请用“;”分隔。`,
    useNumbersFor: (l) => `“${l}”请使用数字。`,
    wholeNumbersFor: (l) => `“${l}”请使用整数，各项之间用“;”分隔。`,
    commaSeparated: '以逗号分隔',
  },
  freshnessUnknown: '我们无法核实您保存的计算是否包含最新的账户变动。在依据这些数字之前，请刷新页面并核对您的账户。',
  goals: { goalN: (n) => `目标 ${n}`, whatMoves: '影响因素 →', earliest: (y) => `最早 ≈ ${y}`, rank: '排序' },
  issues: {
    mainConstraint: '本计划的主要约束',
    riskToReview: '需审视的风险',
    missingInformation: '缺失信息',
    whyItMatters: (t) => `为何重要：${t}`,
    seeEvidence: '查看支持依据 ↓',
    noneFound: '根据本计划目前掌握的信息，已完成的分析未发现重大问题。',
    notAvailable: '问题分析尚未就绪。此处为空并不代表不存在问题。',
    discussQuestion: '请审视本计划发现的问题，说明其依据，并问我：我的情况中是否有缺失的信息可能改变这些问题的优先级或结论。',
    discussLabel: '讨论这些问题',
  },
  policies: {
    optionTested: '已测算的方案',
    computedEffect: (v) => `测算效果：${v}`,
    tradeoff: (v) => `取舍：${v}`,
    backToAction: '返回行动项 ↑',
    tradeoffsHeading: '已测算情形中的取舍',
  },
  transfer: {
    toHeirs: (m) => `继承人多得 +${m}`,
    fundedPoints: (label, pts) => `${label} ${pts} 个百分点`,
    noChange: ' · 对目标资金保障无影响',
  },
  tax: {
    rulesIntro: '仅显示与本次计算关联的规则。每条均显示已记录的来源信息；缺失的日期或适用范围信息会明确标注为缺失。',
    title: '本计划采用的税务处理',
    countryBookPrimary: '预测逐年采用了相应国家的税法。下方列出明确记录为官方或一手来源的资料。',
    countryBookNoPrimary: '预测逐年采用了相应国家的税法。此已保存结果尚未附上一手来源链接，因此在做出涉税决定前请先核实。',
    simplified: '本次计算采用了简化的税务处理。在依据涉税比较之前，请先查看下方的局限说明。',
    askQuestion: '请解释本计划适用了哪些税法和国家层级，哪些采用了近似处理或被排除，以及我的税务状况中哪些事实可能改变结果。只使用本次计算实际附带的一手来源，并指出缺少哪些来源。',
    askLabel: '询问税务处理',
  },
  inputs: { title: '可能改变结果的输入信息', infoToConfirm: '待确认信息' },
  gated: {
    countChoices: (_n, f) => `为您测算的 ${f} 个方案`,
    mappedYears: (_n, f) => `我们推演了未来 ${f} 年`,
    extraChecks: (_n, f) => `对您的计划另做了 ${f} 项检验`,
    placeholderRows: ['示例行', '示例数字', '示例方案', '占位内容'],
  },
}

export default zh
